import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import { gql, useQuery } from '@apollo/client';
import { useRecoilValue } from 'recoil';

import * as K from 'common/constants';
import { Permission } from 'common/graphqlTypes';
import { getUrlParam } from 'common/util/location';
import { THEME_INFO } from 'styles/stylesProvider/themes';

import Page from 'components/common/Page/Page';
import { hasPermissionState } from 'components/account/account.state';
import { themeState } from 'components/common/Managers/SettingsManager.state';

import * as md from 'components/help/markdown';

import { HELP_DIRECTIVE, HELP_FILE, HELP_ROUTE } from './constants';

import HELP_IMAGE from './images';

import HelpNav from './HelpNav';
import './HelpPage.scss';

// an item in a sub-menu for the current page, in the left-side nav section
export type NavLink = {
  name: string;
  text: string;
};

// a command of the form [[ ... ]] that typically interpolates a value
type Directive = {
  directiveName: string;
  directiveArgs?: any;
};

const DIRECTIVE_RE = new RegExp('\\[\\[\\[.*?\\]\\]\\]', 'g');

const DOCS_DATA_QUERY = gql`
  query getDocsData {
    docsData {
      agentVersion {
        agent {
          beta
          internal
          stable
        }
        repo {
          beta
          internal
          stable
        }
      }
      awsExternalId
      awsAccountToTrust
      logsUploadHost
      readLogsToken
      writeLogsToken
    }
  }
`;

type HelpPageProps = {
  isPublic?: boolean;
};

/**
 * Show a help page with navigation on the left side and the help content to the right. The content comes in
 * as markdown which is transformed to JSX. The remark-gfm plugin gives us GitHub-flavored markdown
 * (see https://github.github.com/gfm/). The rehype-raw plugin lets us render trusted HTML.
 *
 * @prop isPublic    if true, this page is public and the user is not authenticated
 */
function HelpPage({ isPublic = false }: HelpPageProps): JSX.Element {
  const [markdown, setMarkdown] = useState('');
  const [navLinks, setNavLinks] = useState<NavLink[]>(K.EMPTY_ARRAY);
  const pageRef = useRef<HTMLDivElement>(null);
  const hasFullAccessPermission = useRecoilValue(hasPermissionState(Permission.fullAccess));

  const theme = useRecoilValue(themeState);
  const isDarkTheme = THEME_INFO[theme].isDark;

  const path = window.location.pathname as HELP_ROUTE;

  const { data } = useQuery(DOCS_DATA_QUERY);
  const docsData = data?.docsData;

  /**
   * A directive consists of a name and optional arguments expressed as JSON, for example:
   *    [[ anchor { "name":"events", "text":"Logs and Events" } ]]
   *
   * This function parses that string and returns the name and an object representing the arguments (it will be
   * empty if there were no arguments).
   *
   * @param {string} directive    help doc directive
   *
   * @returns Directive object with name and arguments
   */
  const parseDirective = (directive: string): Directive => {
    let directiveName = '';
    let directiveArgs = {};
    const m = directive.match(/^\[\[\[\s*(\w+)\s*([^\]]*?)\s*\]\]\]$/);
    if (m) {
      directiveName = m[1];
      try {
        // parse() should be okay here - this is internal content
        directiveArgs = JSON.parse(m[2]);
      } catch (e) {}
    }

    return { directiveName, directiveArgs };
  };

  /**
   * Perform any actions that rely on directives, such as gathering navigation links for a sub-menu. Does not alter content.
   *
   * @param {string} content    markdown
   */
  const processDirectives = (content: string): void => {
    const anchors = [];
    let m;
    while ((m = DIRECTIVE_RE.exec(content)) !== null) {
      const { directiveName, directiveArgs } = parseDirective(m[0]);
      if (directiveName === HELP_DIRECTIVE.ANCHOR && !!directiveArgs.text) {
        anchors.push(directiveArgs);
      }
    }
    setNavLinks(anchors);
  };

  /**
   * Process a single directive and return the text that should replace it.
   *
   * @param {string} directive    help doc directive
   * @returns replacement text
   */
  const replaceDirective = (directiveConfig: string): string => {
    // package name format varies by architecture
    const getPackageName = (format: string) => {
      const version = docsData?.agentVersion?.agent?.stable;
      let win32Version = version;
      const parts = version.split('.');
      if (parts.length === 5) {
        win32Version = [parts[0], parts[1], parts[2], parts[4]].join('.');
      }
      switch (format) {
        case 'rpm':
          return 'scalyr-agent-2-' + version + '-1.noarch.rpm';
        case 'deb':
          return 'scalyr-agent-2_' + version + '_all.deb';
        case 'rhel5':
          return 'scalyr-agent-2-' + version + '-1.alt.noarch.rpm';
        case 'win32':
          return 'ScalyrAgentInstaller-' + win32Version + '.msi';
        case 'tarball':
          return 'scalyr-agent-' + version + '.tar.gz';
        default:
          return '[ agent format "' + format + '" not found ]';
      }
    };

    const getNonDefaultUploadHost = () => {
      const uploadHost = docsData?.logsUploadHost;
      return uploadHost !== 'log.scalyr.com' ? uploadHost : '';
    };

    const { directiveName, directiveArgs } = parseDirective(directiveConfig);
    const directive = directiveName as HELP_DIRECTIVE;

    // if the directive needs server data but we don't have it yet, return empty string
    if (
      ![HELP_DIRECTIVE.ANCHOR, HELP_DIRECTIVE.AUDIT_TRAIL_URL, HELP_DIRECTIVE.PUBLIC_APP_URL].includes(directive) &&
      !docsData
    ) {
      return '';
    }

    const origin = window.location.origin;

    let result;
    if (directive === HELP_DIRECTIVE.AGENT_CONFIG) {
      if (isPublic) {
        result = '[Log in to view configuration instructions.]';
      } else if (!hasFullAccessPermission) {
        result =
          "[You don't have permission to view API tokens for this account which is required for configuration instructions.]";
      } else {
        const uploadHost = getNonDefaultUploadHost();
        const scalyrServer = uploadHost ? ' --set-scalyr-server "https://' + uploadHost + '"' : '';
        result = 'scalyr-agent-2-config --set-key "' + docsData?.writeLogsToken + '"' + scalyrServer;
      }
    } else if (directive === HELP_DIRECTIVE.AGENT_DOWNLOAD_URL) {
      result = origin + '/scalyr-repo/stable/latest/' + getPackageName(directiveArgs.format);
    } else if (directive === HELP_DIRECTIVE.AGENT_PACKAGE_NAME) {
      result = getPackageName(directiveArgs.format);
    } else if (directive === HELP_DIRECTIVE.AGENT_VERSION) {
      result = docsData?.agentVersion?.agent?.stable;
    } else if (directive === HELP_DIRECTIVE.ANCHOR) {
      result = '<a name="' + directiveArgs.name + '"></a>';
    } else if (directive === HELP_DIRECTIVE.AND_SCALYR_SERVER) {
      result = !!getNonDefaultUploadHost() ? ' and ' + K.PRODUCT_NAME + ' upload server' : '';
    } else if (directive === HELP_DIRECTIVE.API_TOKEN_AND_SERVER) {
      if (isPublic) {
        result = '[Log in to see your API token.]';
      } else if (!hasFullAccessPermission) {
        result = "[You don't have permission to view API tokens for this account.]";
      } else {
        result = '{\n      api_key: "' + docsData?.writeLogsToken + '"';
        const uploadHost = getNonDefaultUploadHost();
        if (uploadHost) {
          result = result + ',\n    scalyr_server: "https://' + uploadHost + '"';
        }
        result = result + '\n    }';
      }
    } else if (directive === HELP_DIRECTIVE.AUDIT_TRAIL_URL) {
      result = origin + '/events?mode=log&filter=source%3D%27scalyr%27+tag%3D%27audit%27';
    } else if (directive === HELP_DIRECTIVE.AWS_ACCOUNT) {
      result = docsData?.awsAccountToTrust || '913057016266';
    } else if (directive === HELP_DIRECTIVE.AWS_EXTERNAL_ID) {
      if (isPublic) {
        result = '[Log in to see your External ID.]';
      } else if (!hasFullAccessPermission) {
        result = "[You don't have permission to view the External ID for this account.]";
      } else {
        result = docsData?.awsExternalId;
      }
    } else if (directive === HELP_DIRECTIVE.K8S_SCALYR_SERVER) {
      const uploadHost = getNonDefaultUploadHost();
      return uploadHost ? '--from-literal=scalyr_server=' + uploadHost : '';
    } else if (directive === HELP_DIRECTIVE.LOGS_UPLOAD_HOST) {
      result = docsData?.logsUploadHost;
    } else if (directive === HELP_DIRECTIVE.PUBLIC_APP_URL) {
      result = origin + '/' + (directiveArgs?.path || '');
      if (directiveArgs?.noLink) {
        // keep markdown from recognizing this as a URL
        result = result.replace('http', 'ht<span>tp</span>');
      }
    } else if (directive === HELP_DIRECTIVE.READ_LOGS_TOKEN) {
      if (isPublic) {
        result = '[Log in to see your API token.]';
      } else if (!hasFullAccessPermission) {
        result = "[You don't have permission to view API tokens for this account.]";
      } else {
        result = docsData?.readLogsToken;
      }
    } else if (directive === HELP_DIRECTIVE.TEAM_TOKEN) {
      return 'teamToken=' + getUrlParam(window.location.search, K.URL_PARAM.TEAM_TOKEN);
    } else if (directive === HELP_DIRECTIVE.WRITE_LOGS_TOKEN) {
      if (isPublic) {
        result = '[Log in to see your API token.]';
      } else if (!hasFullAccessPermission) {
        result = "[You don't have permission to view API tokens for this account.]";
      } else {
        result = docsData?.writeLogsToken;
      }
    }

    return result || '';
  };

  // There's no easy way to get the markdown directly as a string without wrapping it in JSX or trying to capture it as a JS string.
  // Turns out that fetch() can be used to return the file contents, which allows the file to remain as pure markdown.
  // See: https://github.com/facebook/create-react-app/issues/2961#issuecomment-322916352
  useEffect(() => {
    if (Object.values(HELP_ROUTE).includes(path)) {
      const fileKey = HELP_FILE[path as unknown as HELP_ROUTE];
      // @ts-expect-error
      const content = (fileKey && md[fileKey]) || '';
      if (content) {
        fetch(content)
          .then((response) => response.text())
          .then((text) => {
            let markdown = text.replace(/\\\n/g, '');
            processDirectives(markdown);
            markdown = markdown.replace(DIRECTIVE_RE, replaceDirective);
            setMarkdown(markdown);
            const page = pageRef?.current;
            // scroll to top when we switch pages (otherwise a short page might be scrolled past its content)
            if (page) {
              setTimeout(() => {
                page.scrollTop = 0;
              }, 0);
            }
          });
      } else {
        setMarkdown('');
      }
    }
  }, [path, docsData]);

  return (
    <Page docTitle="Help" type="full" pageRef={pageRef}>
      <div className="HelpPage">
        <HelpNav path={path} navLinks={navLinks} />
        <div className="HelpPage__Body">
          <ReactMarkdown
            remarkPlugins={[gfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({ ...props }) => {
                const src = props.src as string;
                delete props.src;
                const img = isDarkTheme ? HELP_IMAGE.dark[src] : HELP_IMAGE.light[src];
                return <img className="docImage" alt={K.PRODUCT_NAME} src={img} {...props} />;
              },
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      </div>
    </Page>
  );
}

export default HelpPage;
